import React, { useRef, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { SubmitButton } from '../form-elements';
import { ValidateOTPErrors } from '../../../redux/auth/interfaces';
import { withAnalytics, WithAnalyticsProps } from '../../../HOC/with-analytics';

type VerificationFormProps = {
  phoneNumber: string;
  isValidating: boolean;
  accountVerified?: boolean;
  errors: ValidateOTPErrors;
  title: string;
  resendOTP: (phone: string) => void;
  validateOTP: (pin: string) => void;
} & WithAnalyticsProps;

const maskPhoneNo = (phoneNo: string) => {
  const last3 = phoneNo.substring(phoneNo.length - 3);
  const mask = phoneNo.substring(0, phoneNo.length - 3).replace(/\d/g, '*');

  return `${mask}${last3}`;
};

const OTPInput = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  props.refs.push(inputRef);

  return (
    <div className="form-group flex-1 px-2">
      <input
        type="text"
        name={`val${props.index}`}
        maxLength={1}
        ref={inputRef}
        value={props.value || ''}
        onKeyUp={props.autoTab}
        data-index={props.index}
        onChange={props.handleChange}
        className="w-full text-center bg-gray-300 px-4 py-4 rounded-md placeholder-gray-900"
      />
    </div>
  );
};

const OTPValidationForm: React.SFC<VerificationFormProps> = (props) => {
  const elemRefs = [];
  const [formErrors, setFormErrors] = useState<any>({});

  const initialValues = {
    val0: '',
    val1: '',
    val2: '',
    val3: '',
  };

  const autoTab = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const BACKSPACE_KEY = 8;
    const DELETE_KEY = 46;

    const tabindex = Number((e.target as HTMLInputElement).getAttribute('data-index') || 0);
    let elem = null;

    if (e.keyCode === BACKSPACE_KEY) {
      elem = tabindex > 0 && elemRefs[tabindex - 1];
    } else if (e.keyCode !== DELETE_KEY) {
      elem = tabindex < elemRefs.length - 1 && elemRefs[tabindex + 1];
    }
    if (elem) {
      elem.current.focus();
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit: ({ val0, val1, val2, val3 }, { setSubmitting }) => {
      const pin = `${val0}${val1}${val2}${val3}`;

      if (pin.length < 4) {
        setSubmitting(false);
        return;
      }

      props.validateOTP(pin);
    },
  });

  const resendOTP = () => {
    props.sendAnalytics('clickResendOTP', 'Auth');
    props.resendOTP(props.phoneNumber);
  };

  const _handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    props.sendAnalytics('clickValidateOTP', 'Auth');
    formik.submitForm();
  };

  useEffect(() => {
    if (!props.isValidating) {
      formik.setSubmitting(false);
      setFormErrors(props.errors || {});
    }
  }, [props.isValidating]);

  const { values, isSubmitting, ...formProps } = formik;

  return (
    <div className="px-4 max-w-md mx-auto">
      <p className="text-header text-3xl text-center sm:text-4xl leading-tight tracking-wide font-bold">
        {props.title}
      </p>

      <p className="text-center my-6">
        Enter the code that was sent to {maskPhoneNo(props.phoneNumber || '')}
      </p>

      <form onSubmit={_handleSubmit}>
        <div className="mt-4 flex -mx-2">
          {Array.from({ length: 4 }, (_, index) => (
            <OTPInput
              key={index}
              index={index}
              refs={elemRefs}
              autoTab={autoTab}
              value={values[`val${index}`]}
              handleChange={formik.handleChange}
            />
          ))}
        </div>

        <p className="mt-4">
          Didn’t recieve code? {''}
          <span className="text-secondary cursor-pointer" onClick={resendOTP}>
            Resend Code
          </span>
        </p>

        {formErrors.submit && (
          <div className="p-3 text-red-600 font-light bg-red-300 rounded text-sm my-4">
            {formErrors.submit}
          </div>
        )}

        <SubmitButton
          direction="right"
          title="Verify"
          loadingMsg="Processing..."
          isSubmitting={isSubmitting}
          isDirty={formProps.dirty}
          className="w-1/2 mt-4"
        />
      </form>
    </div>
  );
};

export default withAnalytics(OTPValidationForm);
